import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const NotificationToast = ({ 
  notification, 
  onDismiss,
  autoDismissDelay = 5000 
}) => {
  const [isExiting, setIsExiting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (autoDismissDelay > 0) {
      const timer = setTimeout(() => {
        handleDismiss();
      }, autoDismissDelay);

      return () => clearTimeout(timer);
    }
  }, [autoDismissDelay]);

  const handleDismiss = () => {
    setIsExiting(true);
    setTimeout(() => {
      onDismiss(notification.id);
    }, 300); // Match animation duration
  };

  const handleClick = () => {
    if (notification.navigateTo) {
      navigate(notification.navigateTo);
      handleDismiss();
    }
  };

  // Color classes based on notification type
  const colorClasses = {
    blue: 'bg-blue-50 border-blue-200 text-blue-900',
    purple: 'bg-purple-50 border-purple-200 text-purple-900',
    green: 'bg-green-50 border-green-200 text-green-900',
    amber: 'bg-amber-50 border-amber-200 text-amber-900',
    indigo: 'bg-indigo-50 border-indigo-200 text-indigo-900',
    red: 'bg-red-50 border-red-200 text-red-900',
    teal: 'bg-teal-50 border-teal-200 text-teal-900',
    slate: 'bg-slate-50 border-slate-200 text-slate-900',
    gray: 'bg-gray-50 border-gray-200 text-gray-900'
  };

  const colorClass = colorClasses[notification.color] || colorClasses.gray;

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleString();
  };

  return (
    <div
      className={`
        transform transition-all duration-300 ease-in-out
        ${isExiting 
          ? 'translate-x-full opacity-0' 
          : 'translate-x-0 opacity-100'
        }
      `}
    >
      <div
        className={`
          rounded-lg border shadow-lg p-4 mb-3 min-w-[320px] max-w-[400px]
          ${colorClass}
          ${notification.navigateTo ? 'cursor-pointer hover:shadow-xl' : ''}
          transition-shadow duration-200
        `}
        onClick={notification.navigateTo ? handleClick : undefined}
        role={notification.navigateTo ? 'button' : 'alert'}
        tabIndex={notification.navigateTo ? 0 : undefined}
        onKeyDown={notification.navigateTo ? (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick();
          }
        } : undefined}
      >
        <div className="flex items-start gap-3">
          {/* Icon */}
          <div className="flex-shrink-0 text-2xl">
            {notification.icon}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <h4 className="text-sm font-semibold leading-tight">
                {notification.title}
              </h4>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDismiss();
                }}
                className="flex-shrink-0 text-current opacity-60 hover:opacity-100 transition-opacity"
                aria-label="Dismiss notification"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            
            <p className="mt-1 text-sm opacity-90 line-clamp-2">
              {notification.body}
            </p>

            <p className="mt-2 text-xs opacity-70">
              {formatTimestamp(notification.timestamp)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationToast;
